import { proxy } from 'valtio'
import { devtools, subscribeKey, proxyMap } from 'valtio/utils'
import { nanoid } from 'nanoid'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import cloneDeep from 'lodash/cloneDeep'

import { getHistory, getSession, stopGenarate, getRecommend } from '@/api/chat'
import { SessionStatuTypes, SAFTY_TEXT, RoleTypes } from '@/constants'
import type { IMessage } from '@/types/chat'
import { message as antMessage } from '@/App'
import reportAction from '@/utils/statistic'
import { getRegion } from '@/utils/region'
import { globalState, globalActions } from './global'
import { citationRegex, toCitationDirective } from '@/utils/formate'
import { message } from 'antd'

const wssOrigin = (
  import.meta.env.VITE_APP_COMMON_BASE_URL || location.origin
).replace('https', 'wss')
const socketUrl = `${wssOrigin}/zdtc-api/wspivot/`
const initAssistant = {
  role: RoleTypes.assistant,
  loading: true,
  out_finished: false,
}

// ws 连接实例
let wsInstance: WebSocket | null = null

export enum DeepThinkMode {
  AUTO = 'auto',
  ON = 'on',
  OFF = 'off',
}

export const chatState = proxy<{
  sessionId: string | null
  sessionStatu: SessionStatuTypes
  autoScroll: boolean
  sessionMessages: IMessage[]
  isHistory: boolean
  historySessions: any[] // 历史对话
  inputText: string
  inputFileList: any[]
  inputFileType: string
  edit_asr: number
  asrSessionId: string
  plugins: Map<string, any>
  deepThinkMode: DeepThinkMode
  interactiveMode: boolean
  refreshHistory: boolean
  userScrolled: boolean
}>({
  // 当前会话 id
  sessionId: nanoid(),
  // 会话状态
  sessionStatu: SessionStatuTypes.ready,
  // 会话消息列表
  sessionMessages: [],
  // 是否是历史会话
  isHistory: false,
  // 历史消息记录
  historySessions: [],
  // 输入框内容
  inputText: '',
  // 输入的文件
  inputFileType: '',
  inputFileList: [],
  edit_asr: 0,
  // 语音识别的输入关联
  asrSessionId: '',
  // 插件列表
  plugins: proxyMap(),
  // 回答完成
  autoScroll: true,
  deepThinkMode:
    (localStorage.getItem('chat-taichu-deepThinkMode') as DeepThinkMode) ||
    DeepThinkMode.AUTO,
  interactiveMode: false,
  refreshHistory: false,
  userScrolled: false,
})

if (import.meta.env.DEV) {
  devtools(chatState, { name: 'chatState', enabled: true })
}

// 风控匹配
subscribeKey(chatState, 'sessionMessages', val => {
  const item = val.find((i: any) => (i?.text || '').includes(SAFTY_TEXT))
  if (item) {
    chatState.refreshHistory = true
    chatAction.updateSessionStatu(SessionStatuTypes.risk)
  }
})

export const chatAction = {
  setInputText: (text: string) => {
    chatState.inputText = text
  },
  updateSessionStatu: (statu: SessionStatuTypes) => {
    chatState.sessionStatu = statu
  },
  updateAutoScroll: (statu: boolean) => {
    chatState.autoScroll = statu
  },
  reset: () => {
    chatState.sessionStatu = SessionStatuTypes.ready
    chatState.sessionMessages = []
    chatState.asrSessionId = ''
    chatState.inputText = ''
    chatState.inputFileType = ''
    chatState.inputFileList = [] // 目前上传文件和sessionId绑定，所以新建会话时清空文件
    chatState.plugins.clear()
    chatState.interactiveMode = false
    globalActions.pauseCurrentAudio()
  },
  createNewSession: () => {
    chatState.sessionId = nanoid()
    chatState.isHistory = false
    chatAction.reset()
  },
  getHistorySession: (sessionId: string) => {
    if (
      chatState.sessionStatu === SessionStatuTypes.pending ||
      chatState.sessionStatu === SessionStatuTypes.loading
    ) {
      message.warning('对话正在进行中')
      return
    }
    chatState.sessionId = sessionId
    chatState.isHistory = true
    globalActions.pauseCurrentAudio()

    getSession(sessionId)
      .then(({ data }) => {
        chatAction.reset()
        const messages = data?.chats || []
        messages.forEach((item: any) => {
          const toolResponse = item.events.find(
            (i: any) => i.event === 'toolResponse',
          )
          if (citationRegex.test(item.text) && toolResponse) {
            const response = JSON.parse(
              toolResponse?.data.tool?.response || '{}',
            )
            item.text = toCitationDirective(item.text, response.news || [])
          }
          if ((item?.text || '').includes(SAFTY_TEXT)) {
            item.reasoning_content = ''
          }
        })
        chatAction.updateMessages(messages)

        if (data?.plugins.length) {
          chatState.plugins.set(data?.plugins[0].type, true)
        }
      })
      .catch(err => {
        console.log('err :>> ', err)
      })
  },
  getHistoryRecord: () => {
    getHistory()
      .then(({ data }) => {
        chatState.historySessions = data.sessions || []
      })
      .catch(err => {
        console.log('err :>> ', err)
      })
  },
  updateMessages: (messages: IMessage[]) => {
    chatState.sessionMessages = messages
  },
  updateUserScrolled: (scrolled: boolean) => {
    chatState.userScrolled = scrolled
  },
  onUserSubmit: (input: IMessage) => {
    chatAction.updateAutoScroll(true)
    chatAction.updateUserScrolled(false)
    const messages = cloneDeep(chatState.sessionMessages)
    if (chatState.sessionStatu === SessionStatuTypes.risk) {
      antMessage.open({
        key: 'chat',
        type: 'warning',
        content: '请新建会话再发送',
      })
      return
    }
    if (chatState.sessionStatu !== SessionStatuTypes.ready) {
      antMessage.open({
        key: 'chat',
        type: 'warning',
        content: '请等待回复后再发送',
      })
      return
    }
    if (!chatState.sessionId) {
      chatState.sessionId = nanoid()
    }
    let sendMessage = {
      ...input,
      question_id: input.question_id || nanoid(),
      role: RoleTypes.user,
      reAnswer: 0,
    }

    messages.push(sendMessage)
    console.log('onUserSubmit input :>> ', input)

    if (!input.noSend) {
      messages.push(initAssistant)
    }
    chatAction.updateMessages(messages)
    console.log('onUserSubmit messages :>> ', {
      ...sendMessage,
      combine_file: true,
      session_id: chatState.sessionId,
      // 交互模式下强制关闭 auto_cot，否则按 deepThinkMode 判断
      auto_cot:
        !chatState.interactiveMode &&
        chatState.deepThinkMode === DeepThinkMode.AUTO,
      // 交互模式下强制开启 thinking，否则按 deepThinkMode 判断
      thinking_enabled:
        chatState.interactiveMode ||
        chatState.deepThinkMode !== DeepThinkMode.OFF,
      in_queue: 0,
      auth_code: globalState.userInfo.authCode,
      region: getRegion(),
    })
    if (!input.noSend) {
      reportAction(
        {
          action: 'chat',
          user_id: globalState.userInfo.id,
        },
        {
          session_id: chatState.sessionId,
        },
      )
      chatAction.connectChatWs(sendMessage)
    }
  },
  reAnswer: (question_id: string) => {
    const messages = cloneDeep(chatState.sessionMessages)
    const question = messages.find(
      (i: IMessage) =>
        i.role === RoleTypes.user && i.question_id === question_id,
    )
    const answer = messages.find(
      (i: IMessage) =>
        i.role === RoleTypes.assistant && i.question_id === question_id,
    )

    messages.splice(-1, 1, {
      ...initAssistant,
      answer_list: (answer?.answer_list || []).concat([
        omit(answer, 'answer_list'),
      ]),
    })
    chatAction.updateUserScrolled(true)
    chatAction.updateMessages(messages)
    chatAction.connectChatWs({
      ...pick(question, ['audio', 'pic', 'video', 'scene']),
      text: question?.text,
      question_id: question?.question_id,
      reAnswer: 1,
    })
  },
  connectChatWs: (input: IMessage) => {
    wsInstance = new WebSocket(socketUrl)
    let text = ''
    let reasoning_content = ''
    let events: any = []
    chatAction.updateSessionStatu(SessionStatuTypes.loading)
    wsInstance.onopen = () => {
      const sendData = {
        ...input,
        combine_file: true,
        session_id: chatState.sessionId,
        // 交互模式下强制关闭 auto_cot，否则按 deepThinkMode 判断
        auto_cot:
          !chatState.interactiveMode &&
          chatState.deepThinkMode === DeepThinkMode.AUTO,
        // 交互模式下强制开启 thinking，否则按 deepThinkMode 判断
        thinking_enabled:
          chatState.interactiveMode ||
          chatState.deepThinkMode !== DeepThinkMode.OFF,
        in_queue: 0,
        auth_code: globalState.userInfo.authCode,
        region: getRegion(),
      }
      wsInstance?.send(JSON.stringify(sendData))
      chatAction.updateSessionStatu(SessionStatuTypes.pending)
    }
    wsInstance.onmessage = msg => {
      let res = JSON.parse(msg.data)
      if (res.status === 1 || res.message === 'success') {
        if (res.data?.session !== chatState.sessionId) {
          return
        }
        if (res.data.event?.event) {
          events = events.concat(res.data.event)
        }

        text = `${text}${res.data.text}`
        reasoning_content = `${reasoning_content}${res.data.reasoning_content}`
        const toolResponse = events.find((i: any) => i.event === 'toolResponse')
        if (citationRegex.test(text) && toolResponse) {
          const response = JSON.parse(toolResponse?.data.tool?.response || '{}')
          const citationText = toCitationDirective(
            // res.data.text,
            text,
            response.news || [],
          )
          text = `${citationText}`
        }
      } else {
        chatAction.updateAutoScroll(true)
        antMessage.open({
          key: 'chat',
          type: 'error',
          content: res.message,
        })
        // 400 账号被封禁 401: 登录过期 402:禁言 403:ip 封禁
        if ([-1, 401, 403, 400].includes(res.status)) {
          globalActions.logout()
          chatAction.createNewSession()
        } else if (res.status === 402) {
          text = res.message || '账号违规禁言'
        } else {
          text = res.message || '当前服务繁忙，请稍后再试！'
        }
      }

      const { out_finished, answer_id, risklevel, riskMessage } = res.data || {}
      const messages = cloneDeep(chatState.sessionMessages)
      let lastMessage = messages[messages.length - 1] || {}
      const isSuccess = res.status === 1 || res.message === 'success'
      const isRisk = isSuccess && (!risklevel || !!riskMessage)
      const shouldHideThinking = !isSuccess || isRisk
      const displayText = isSuccess
        ? !risklevel
          ? riskMessage || SAFTY_TEXT
          : text
        : text

      lastMessage = {
        ...lastMessage,
        ...res.data,
        evalutetype: 0,
        loading: false,
        events,
        text: displayText,
        reasoning_content: shouldHideThinking ? '' : reasoning_content,
        thinking_elapsed_secs:
          res.data?.thinking_elapsed_secs ?? lastMessage.thinking_elapsed_secs,
      }

      messages.splice(messages.length - 1, 1, lastMessage)
      chatAction.updateMessages(messages)

      if (
        res.status !== 1 ||
        out_finished ||
        res.data?.stop === 1 ||
        riskMessage
      ) {
        chatAction.updateAutoScroll(true)
        wsInstance?.close()
      }

      if (
        risklevel &&
        out_finished &&
        chatState.sessionId &&
        answer_id &&
        !reasoning_content
      ) {
        chatAction.getRecommend(chatState.sessionId, answer_id)
      }
    }
    wsInstance.onclose = () => {
      console.log('关闭连接')
      chatState.refreshHistory = true
      if (chatState.sessionStatu !== SessionStatuTypes.risk) {
        chatAction.updateSessionStatu(SessionStatuTypes.ready)
      }
      // 如果当前会话不在历史记录第一条，刷新历史记录列表
      // if (chatSideBarState.historySessions[0]?.sessionId !== chatState.sessionId) {
      //   chatSideBarAction.getHistoryRecord()
      // }
      // onMessageFaild()
    }
    wsInstance.onerror = () => {
      console.log('连接失败')
      chatAction.updateSessionStatu(SessionStatuTypes.ready)
      onMessageFaild()
    }
  },
  closeConnect: async (question_id: string, ispush: boolean = false) => {
    await stopGenarate({
      session_id: chatState.sessionId,
      question_id,
    })
    wsInstance?.close()
    const messages = cloneDeep(chatState.sessionMessages)
    let lastMessage = messages[messages.length - 1] || {}
    lastMessage = {
      ...lastMessage,
      loading: false,
      out_finished: true,
      stop: 1,
    }
    messages.splice(messages.length - 1, 1, lastMessage)
    chatAction.updateMessages(messages)
  },
  getRecommend: async (session_id: string, answer_id: string) => {
    try {
      const { data } = await getRecommend(session_id)

      const messages = cloneDeep(chatState.sessionMessages)
      let lastMessage = messages[messages.length - 1] || {}
      console.log('===>', {
        recommand_answer_id: answer_id,
        last_answer_id: lastMessage.answer_id,
      })
      if (lastMessage.answer_id !== answer_id) {
        return
      }
      lastMessage = {
        ...lastMessage,
        related: data ?? [],
      }
      messages.splice(messages.length - 1, 1, lastMessage)
      chatAction.updateMessages(messages)
    } catch (error) {
      const messages = cloneDeep(chatState.sessionMessages)
      let lastMessage = messages[messages.length - 1] || {}
      if (lastMessage.answer_id !== answer_id) {
        return
      }
      lastMessage = {
        ...lastMessage,
        related: [],
      }
      messages.splice(messages.length - 1, 1, lastMessage)
      chatAction.updateMessages(messages)
    }
  },
  updateDeepThinkMode: (mode: DeepThinkMode) => {
    localStorage.setItem('chat-taichu-deepThinkMode', mode)
    chatState.deepThinkMode = mode
  },
}

const onMessageFaild = () => {
  const messages = cloneDeep(chatState.sessionMessages)
  let lastMessage = messages[messages.length - 1] || {}
  if (lastMessage.loading) {
    lastMessage = {
      ...lastMessage,
      loading: false,
      text: '当前服务繁忙，请稍后再试！',
    }
    messages.splice(messages.length - 1, 1, lastMessage)
    chatAction.updateMessages(messages)
  }
}
